// Sprint 17 A8: Pipeline AI Indicators — summary bar above kanban
import { Brain, AlertTriangle, TrendingUp, Clock, Target } from "lucide-react";
import { Skeleton, SkeletonCard } from "@/components/skeleton";
import { usePipelineAI } from "@/hooks/use-crm-pipeline-ai";
import type { StageMetrics } from "@/hooks/use-crm-pipeline-ai";
import { DealVelocityChart } from "@/components/crm/deal-velocity-chart";

interface PipelineAIBarProps {
  pipelineId: string | undefined;
}

export function PipelineAIBar({ pipelineId }: PipelineAIBarProps) {
  const { data, isLoading } = usePipelineAI(pipelineId);

  if (isLoading) {
    return (
      <div className="mb-4 space-y-3">
        <Skeleton className="h-4 w-64" />
        <div className="grid grid-cols-2 gap-3 md:grid-cols-4">
          {[1, 2, 3, 4].map((i) => (
            <SkeletonCard key={i} className="space-y-2" />
          ))}
        </div>
      </div>
    );
  }

  if (!data?.stages?.length) return null;

  const bottlenecks = data.stages.filter((s) => s.isBottleneck);

  return (
    <div className="space-y-3 mb-4">
      {/* Summary metrics */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
        <MetricCard
          Icon={Target}
          label="Pipeline Value"
          value={formatCurrency(data.totalPipelineValue)}
          color="text-[var(--color-primary)]"
        />
        <MetricCard
          Icon={TrendingUp}
          label="Win Rate"
          value={`${Math.round(data.overallWinRate)}%`}
          color="text-[var(--color-success)]"
        />
        <MetricCard
          Icon={Clock}
          label="Avg Cycle"
          value={`${data.avgCycleTimeDays}d`}
          color="text-[var(--color-warning)]"
        />
        <MetricCard
          Icon={AlertTriangle}
          label="Bottlenecks"
          value={String(bottlenecks.length)}
          color={bottlenecks.length > 0 ? "text-[var(--color-destructive)]" : "text-[var(--color-success)]"}
        />
      </div>

      {/* Stage conversion funnel */}
      <div className="flex items-center gap-1 overflow-x-auto">
        {data.stages.map((stage, idx) => (
          <StageIndicator key={stage.stageId} stage={stage} isLast={idx === data.stages.length - 1} />
        ))}
      </div>

      {/* AI Summary */}
      <div className="rounded-lg border border-[var(--color-ai-border)] bg-[var(--color-ai-light)] p-3 flex items-start gap-2">
        <Brain className="h-4 w-4 text-[var(--color-ai)] mt-0.5 flex-shrink-0" />
        <p className="text-xs text-[var(--color-foreground)]">{data.aiSummary}</p>
      </div>

      <DealVelocityChart
        stages={data.stages.map((stage) => ({
          stageId: stage.stageId,
          stageName: stage.stageName,
          avgDays: stage.avgDaysInStage,
          dealCount: stage.dealCount,
          isBottleneck: stage.isBottleneck,
          comparisonAvgDays: data.avgCycleTimeDays > 0 ? data.avgCycleTimeDays : null,
        }))}
        overallAvgDays={data.avgCycleTimeDays}
        title="Deal velocity"
        emptyCopy="No data"
        comparisonLabel="pipeline avg"
      />
    </div>
  );
}

function MetricCard({ Icon, label, value, color }: {
  Icon: typeof Target;
  label: string;
  value: string;
  color: string;
}) {
  return (
    <div className="stagger-item rounded-lg border border-[var(--color-border)] bg-[var(--color-card)] p-3">
      <div className="flex items-center gap-1.5 mb-1">
        <Icon className={`h-3.5 w-3.5 ${color}`} />
        <span className="text-[10px] text-[var(--color-muted-foreground)]">{label}</span>
      </div>
      <span className="text-lg font-bold text-[var(--color-foreground)]">{value}</span>
    </div>
  );
}

function StageIndicator({ stage, isLast }: { stage: StageMetrics; isLast: boolean }) {
  const convPct = Math.round(stage.conversionRate);

  return (
    <div className="stagger-item flex flex-shrink-0 items-center gap-1">
      <div className={`rounded-md px-2 py-1 text-center min-w-[80px] border ${
        stage.isBottleneck
          ? "border-[var(--color-destructive)]/30 bg-[var(--color-destructive)]/5"
          : "border-[var(--color-border)] bg-[var(--color-card)]"
      }`}>
        <p className="text-[10px] text-[var(--color-muted-foreground)] truncate">{stage.stageName}</p>
        <p className="text-xs font-bold text-[var(--color-foreground)]">{stage.dealCount}</p>
        <p className="text-[9px] text-[var(--color-muted-foreground)]">{stage.avgDaysInStage}d avg</p>
        {stage.isBottleneck && (
          <AlertTriangle className="h-3 w-3 text-[var(--color-destructive)] mx-auto mt-0.5" />
        )}
      </div>
      {!isLast && (
        <div className="flex flex-col items-center">
          <span className="text-[9px] text-[var(--color-muted-foreground)]">{convPct}%</span>
          <div className="w-4 h-px bg-[var(--color-border)]" />
        </div>
      )}
    </div>
  );
}

function formatCurrency(cents: number): string {
  if (cents >= 100000000) return `€${(cents / 100000000).toFixed(1)}M`;
  if (cents >= 100000) return `€${Math.round(cents / 100000)}K`;
  return `€${Math.round(cents / 100)}`;
}
